import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { ArrowLeft, Upload } from "lucide-react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { createAirportService } from "@/lib/airportServicesApi";

interface JsonAirportService {
  id: number;
  service_code: string;
  airport: string;
  service_name: string;
  description?: string;
  service_type: string;
  price_op: string;
  price_ag: string;
  price_cf: string;
  supplier_name?: string;
  cost_supplier?: string;
  tax_percentage?: string;
  hcte_notes?: string;
  pricing_type?: string;
}

const ImportAirportServices = () => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<{ 
    success: number; 
    errors: number; 
    skipped: number;
    errorDetails: Array<{ service_code: string; error: string }>;
  } | null>(null);
  const { userRole } = useAuth();
  const navigate = useNavigate();

  const canImport = userRole === 'ceo' || userRole === 'general_manager';

  if (!canImport) {
    return (
      <DashboardLayout>
        <Alert>
          <AlertDescription>
            Only CEO and General Manager can import airport services.
          </AlertDescription>
        </Alert>
      </DashboardLayout>
    );
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === "application/json") {
        setFile(selectedFile);
        setResults(null);
      } else {
        toast.error("Please select a JSON file");
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setImporting(true);
    setProgress(0);
    let successCount = 0;
    let errorCount = 0;
    const skippedCount = 0;
    const errorDetails: Array<{ service_code: string; error: string }> = [];

    try {
      const fileContent = await file.text();
      const parsed = JSON.parse(fileContent);
      const jsonData: JsonAirportService[] = Array.isArray(parsed)
        ? parsed
        : (parsed?.airport_services || parsed?.items || parsed?.data || parsed?.services || []);

      if (!Array.isArray(jsonData)) {
        throw new Error("Invalid JSON format. Expected an array of airport services.");
      }

      for (let i = 0; i < jsonData.length; i++) {
        const service = jsonData[i];
        
        try {
          const serviceData = {
            id: service.id,
            service_code: service.service_code?.trim() || '',
            airport: service.airport?.trim() || '',
            service_name: service.service_name?.trim() || '',
            description: service.description?.trim() || null,
            service_type: (service.service_type || '').toLowerCase() as 'arrival' | 'departure' | 'transit',
            price_op: parseFloat(service.price_op) || 0,
            price_ag: parseFloat(service.price_ag) || 0,
            price_cf: parseFloat(service.price_cf) || 0,
            supplier_name: service.supplier_name?.trim() || null,
            cost_supplier: service.cost_supplier ? parseFloat(service.cost_supplier) : null,
            tax_percentage: service.tax_percentage ? parseFloat(service.tax_percentage) : null,
            hcte_notes: service.hcte_notes?.trim() || null,
            pricing_type: ((service.pricing_type || 'per_person') as string).toLowerCase() as 'per_person' | 'per_family' | 'per_group'
          };

          let created = false;
          try {
            await createAirportService(serviceData);
            created = true;
          } catch (error: any) {
            const errorMsg = error?.message || 'Failed to import service';
            console.error(`Error importing service ${service.service_code}:`, {
              error,
              serviceData
            });
            errorDetails.push({
              service_code: service.service_code || `Row ${i + 1}`,
              error: errorMsg
            });
            errorCount++;
          }

          if (created) {
            successCount++;
          }
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : 'Unknown error';
          console.error(`Error processing service:`, err);
          errorDetails.push({
            service_code: service.service_code || `Row ${i + 1}`,
            error: errorMsg
          });
          errorCount++;
        }

        setProgress(Math.round(((i + 1) / jsonData.length) * 100));
      }

      setResults({ success: successCount, errors: errorCount, skipped: skippedCount, errorDetails });
      
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} airport services`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} airport services`);
      }
    } catch (error) {
      console.error("Error reading or parsing file:", error);
      toast.error("Error reading or parsing the JSON file");
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Import Airport Services</CardTitle>
            <CardDescription>
              Upload a JSON file containing airport services data to import into the database
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="file-upload" className="block text-sm font-medium">
                Select JSON File
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="block w-full text-sm text-muted-foreground
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary file:text-primary-foreground
                  hover:file:bg-primary/90"
                disabled={importing}
              />
              {file && (
                <p className="text-sm text-muted-foreground">
                  Selected: {file.name}
                </p>
              )}
            </div>

            {importing && (
              <div className="space-y-2">
                <Progress value={progress} />
                <p className="text-sm text-center text-muted-foreground">
                  Importing... {progress}%
                </p>
              </div>
            )}

            {results && (
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Import Results:</p>
                    <p className="text-green-600">✓ Successfully imported: {results.success}</p>
                    {results.errors > 0 && (
                      <>
                        <p className="text-red-600">✗ Failed: {results.errors}</p>
                        {results.errorDetails.length > 0 && (
                          <div className="mt-3 space-y-1 text-sm">
                            <p className="font-semibold text-red-600">Error Details:</p>
                            {results.errorDetails.map((detail, idx) => (
                              <div key={idx} className="pl-2 border-l-2 border-red-300">
                                <p className="font-medium">{detail.service_code}</p>
                                <p className="text-muted-foreground">{detail.error}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                    {results.skipped > 0 && (
                      <p className="text-yellow-600">⊘ Skipped: {results.skipped}</p>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleImport}
                disabled={!file || importing}
                className="flex-1"
              >
                <Upload className="mr-2 h-4 w-4" />
                Import Airport Services
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/airport-services")}
                disabled={importing}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Airport Services
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ImportAirportServices;
